import {Component, DestroyRef, ElementRef, inject, OnInit, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {DatePipe, DOCUMENT, NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {catchError, finalize, forkJoin, map, Observable, of, Subscription, switchMap} from 'rxjs';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatIcon} from '@angular/material/icon';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import moment, {Moment} from 'moment';
import {BenchOverviewService} from '../../../services/bench-overview.service';
import {
  acceptedCommitmentFrom, benchCategories, BenchEntryDTO, BenchFirm, BenchType, BusinessUnitDTO, hasCommitmentDate,
  NoteDTO, OfferAttachmentDTO, OfferDTO, OfferRequiredField, REQUEST_STATUSES, RequestStatus, SalesPersonDTO, VolumeUnit,
} from '../../../shared/model/bench-entry.model';
import {
  fileIcon, fileKind, fileNameFromContentDisposition, formatFileSize, OFFER_ATTACHMENT_MAX_BYTES, sanitizeFileName,
  saveBlob,
} from '../../../shared/attachment-download';
import {BuBadgeComponent} from '../../../shared/components/bu-badge/bu-badge.component';
import {ExtensionBadgeComponent} from '../../../shared/components/extension-badge/extension-badge.component';
import {CompanyBadgeComponent} from '../../../shared/components/company-badge/company-badge.component';
import {MessageService} from '../../../shared/components/message.service';
import {ModalComponent} from '../../../shared/components/modal/modal.component';
import {DateService} from '../../../services/date.service';
import {isEnglish} from '../../../services/format-datepicker';
import {CompanyService} from '../../../services/company.service';
import {INPUT_LIMITS} from '../../../shared/constants/input-limits';
import {LazyImageDirective} from '../../../shared/lazy-image.directive';
import {IProjekt, isDraft} from '../../../shared/model/projekt.model';
import {BeraterService} from '../../../services/berater.service';
import {
  buildSalesIndex, SalesIndex, SalesSubstitution, SalesSubstitutionService,
} from '../../../services/sales-substitution.service';
import {salesParamsOf} from '../../../services/sales-filter-state.service';
import {
  adoptSectionResponse, isOfferChanged, normalizedOffer, notesConflict, notesPayload, offerConflicts, offerPayload,
  SavedSection, withoutOffer,
} from './bench-detail-section-save';

type OfferStatusFilter = 'ALL' | RequestStatus;

interface ProjectFields {
  role: string;
  customer: string;
}

export interface ProjectFieldChange {
  labelKey: string;
  from: string;
  to: string;
}

/** Wählbare anbietende Person eines Angebots. */
export interface SubmitterOption {
  userId: string;
  abbreviation: string;
}

/** NP20-778: Leere Bemerkungen nicht mitspeichern — "Bemerkung hinzufügen" legt eine leere Zeile an. */
const withText = (notes: NoteDTO[]): NoteDTO[] => (notes ?? []).filter(n => !!n.text?.trim());

/**
 * NP20-1184: Wert, den ein Wechsel auf den Status setzt. Rueckmeldung, Abgelehnt und Geschlossen
 * fehlen bewusst — dort bleibt der zuletzt gesetzte Wert stehen.
 */
const PROBABILITY_BY_STATUS: Partial<Record<RequestStatus, number>> = {
  OPEN: 10,
  ACCEPTED: 90,
  PROJECT_CREATED: 100,
};

/** Status, deren Wert nicht von Hand geaendert werden kann. "Offen" gibt nur den Startwert vor. */
const STATUS_BOUND_PROBABILITY: ReadonlySet<RequestStatus> = new Set<RequestStatus>(['ACCEPTED', 'PROJECT_CREATED']);

export type ProjectCreatedMode = 'extend' | 'new';

interface ExtendChoice {
  mode: ProjectCreatedMode;
  projectUuid: string | null;
  newEnd: string | null;
  newStart: string | null;
  newModeEnd: string | null;
}

/** NP20-1290: die Annahme am Angebot; dazu der Projekt-Verweis, den der Verlängerungsdialog setzt. */
const ESTIMATE_KEYS = ['projectStart', 'projectEnd', 'volume', 'volumeUnit', 'extendProjectUuid'] as const;
/** Der Dialog überschreibt beim Bestätigen zusätzlich „Verlängert bis“; Abbrechen stellt es mit zurück. */
const DIALOG_KEYS = [...ESTIMATE_KEYS, 'extendedUntil'] as const;
type EstimateSnapshot = Partial<Pick<OfferDTO, (typeof DIALOG_KEYS)[number]>>;

/** Fehlende Keys bleiben fehlend, sonst gilt das Angebot im JSON-Vergleich als geändert. */
const takeEstimate = (from: Partial<OfferDTO>, keys: readonly (keyof OfferDTO)[] = ESTIMATE_KEYS): EstimateSnapshot => {
  const snapshot: Record<string, unknown> = {};
  for (const key of keys) if (key in from) snapshot[key] = from[key];
  return snapshot as EstimateSnapshot;
};

const applyEstimate = (
  offer: OfferDTO, snapshot: EstimateSnapshot, keys: readonly (keyof OfferDTO)[] = ESTIMATE_KEYS,
): void => {
  const target = offer as unknown as Record<string, unknown>;
  for (const key of keys) {
    if (key in snapshot) target[key] = snapshot[key];
    else delete target[key];
  }
};

const isoDay = (value: Date | string | null | undefined): string | null => {
  if (!value) return null;
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
  }
  return value.slice(0, 10);
};

/** Ein Angebot steht für sich selbst: dort zeigt die Zeile nach dem Speichern kurz „gespeichert“. */
type SavedArea = 'all' | 'notes' | 'offers' | OfferDTO;

/** `skip`: nichts zu senden, der frische Stand gilt als Antwort (Angebot schon von anderen entfernt). */
type SectionStep = {kind: 'put'; payload: BenchEntryDTO} | {kind: 'conflict'} | {kind: 'skip'};

type SectionSaveResult =
  | {kind: 'saved'; entry: BenchEntryDTO; fresh: BenchEntryDTO}
  | {kind: 'conflict'}
  | {kind: 'stale'};

type SaveScope = {kind: 'all'} | {kind: 'offer'; offer: OfferDTO};

type SectionToSave = {kind: 'notes'} | {kind: 'offer'; offer: OfferDTO} | {kind: 'remove'; offer: OfferDTO};

const normalizedCustomer = (customer: string | null | undefined): string => (customer ?? '').trim().toLowerCase();

@Component({
  selector: 'app-bench-detail',
  imports: [
    DatePipe,
    NgClass,
    FormsModule,
    TranslatePipe,
    MatProgressSpinner,
    MatIcon,
    MatButton,
    MatIconButton,
    MatTooltip,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    LazyImageDirective,
    BuBadgeComponent,
    ExtensionBadgeComponent,
    CompanyBadgeComponent,
    ModalComponent,
  ],
  templateUrl: './bench-detail.component.html',
  styleUrls: ['./bench-detail.component.scss', './bench-detail-switch.component.scss', './bench-detail-extend.component.scss'],
  standalone: true,
})
export class BenchDetailComponent implements OnInit {
  protected get LIMITS() { return INPUT_LIMITS; }
  private readonly benchService = inject(BenchOverviewService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly dateService = inject(DateService);
  private readonly companyService = inject(CompanyService);
  private readonly substitution = inject(SalesSubstitutionService);
  private readonly beraterService = inject(BeraterService);

  @ViewChild('notesSection') private notesSection?: ElementRef<HTMLElement>;
  @ViewChild('notesTitle') private notesTitle?: ElementRef<HTMLElement>;
  @ViewChild('offersTitle') private offersTitle?: ElementRef<HTMLElement>;
  private readonly document = inject(DOCUMENT);

  categoriesOf(entry: BenchEntryDTO): BenchType[] {
    return benchCategories(entry);
  }

  isApplicant(entry: BenchEntryDTO): boolean {
    return benchCategories(entry).includes('APPLICANT');
  }

  /** NP20-797: Ab der Zusage bleibt das Datum sichtbar und pflegbar, auch bei „Projekt angelegt". */
  protected readonly hasCommitmentDate = hasCommitmentDate;

  /** NP20-774: gleiche Ableitung wie die "Zusage ab"-Spalte der Übersicht; null blendet die Kachel aus. */
  get commitmentFrom(): string | null {
    return this.entry ? acceptedCommitmentFrom(this.entry) : null;
  }

  entry: BenchEntryDTO | null = null;
  salesPersons: SalesPersonDTO[] = [];
  private salesIndex: SalesIndex = buildSalesIndex([]);
  businessUnits: BusinessUnitDTO[] = [];
  buById = new Map<string, BusinessUnitDTO>();
  isLoading = true;
  isSaving = false;
  showSaved = false;
  /**
   * Bereich, in dem nach dem Speichern kurz „gespeichert“ steht. Oben und unten steht es nur nach
   * „Alles speichern“ oder wenn nichts mehr offen ist, sonst bleibt dort „ungespeichert“.
   */
  savedArea: SavedArea = 'all';

  offerSearch = '';
  offerStatusFilter: OfferStatusFilter = 'ALL';
  offerNewestFirst = true;
  expandedOffers = new Set<OfferDTO>();
  /** Ohne gepflegten Kostensatz angelegt — nur an diesen Angeboten steht der Hinweis (NP20-934). */
  private readonly offersWithoutCostRate = new Set<OfferDTO>();
  /**
   * Zuletzt gelesene Vorbelegung. Liegt bewusst neben dem Eintrag: der Snapshot fuer isDirty
   * ist JSON.stringify(entry), ein Nachlesen auf dem Eintrag liesse ihn als geaendert gelten.
   */
  private suggestedRates: { remote: number | null; onSite: number | null } = {remote: null, onSite: null};
  isAddingOffer = false;

  readonly statusOptions: readonly RequestStatus[] = REQUEST_STATUSES;
  /** NP20-1184: nur die anklickbaren Werte — 90 und 100 setzt allein der Status. */
  readonly probabilityOptions = [10, 25, 50, 75];
  readonly volumeUnitOptions: VolumeUnit[] = ['DAYS', 'HOURS'];
  readonly interviewOptions = ['', 'ja', 'nein'];
  readonly offerFilterChips: { key: OfferStatusFilter; labelKey: string }[] = [
    {key: 'ALL', labelKey: 'benchDetail.offers.filterAll'},
    {key: 'OPEN', labelKey: 'benchOverview.status.open'},
    {key: 'FEEDBACK', labelKey: 'benchOverview.status.feedback'},
    {key: 'REJECTED', labelKey: 'benchOverview.status.rejected'},
    {key: 'ACCEPTED', labelKey: 'benchOverview.status.accepted'},
    {key: 'PROJECT_CREATED', labelKey: 'benchOverview.status.project_created'},
    {key: 'CLOSED', labelKey: 'benchOverview.status.closed'},
  ];

  private savedTimeout: ReturnType<typeof setTimeout> | null = null;
  private originalSnapshot = '';
  private baseCache: {snapshot: string; entry: BenchEntryDTO | null; offers: Map<string, OfferDTO>} =
    {snapshot: '', entry: null, offers: new Map()};
  /** NP20-1229: „Angebot speichern“ beschränkt die Projekt-Rückfragen und das Speichern auf ein Angebot. */
  private saveScope: SaveScope = {kind: 'all'};

  private persistedProjectCreatedOfferIds = new Set<string>();
  private persistedProjectFields = new Map<string, ProjectFields>();
  projectCreatedConfirmOpen = false;
  projectSyncConfirmOpen = false;
  private projectSyncAccepted = false;

  projectsLoadState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
  consultantProjects: IProjekt[] = [];
  private readonly extendChoices = new Map<OfferDTO, ExtendChoice>();
  private projectsSubscription: Subscription | null = null;
  /** NP20-1290: Stand der Angebote beim Öffnen von „Projekt angelegt“; Abbrechen stellt ihn wieder her. */
  private readonly dialogEstimateSnapshot = new Map<OfferDTO, EstimateSnapshot>();

  ngOnInit(): void {
    // Subscribe to paramMap (not snapshot) so future in-detail navigation
    // (next/previous entry) reloads without recreating the component.
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const id = params.get('id');
        if (!id) {
          this.returnToOrigin();
          return;
        }
        this.loadEntry(id);
      });
  }

  private loadEntry(id: string): void {
    this.isLoading = true;
    // A (re)load swaps in a fresh entry, so drop state tied to the previous one.
    this.momentCache.clear();
    this.expandedOffers.clear();
    this.offersWithoutCostRate.clear();
    // Ein noch laufendes Nachlesen gehoert zum vorigen Eintrag und legt hier nichts mehr an.
    this.isAddingOffer = false;
    this.uploadingOffers.clear();
    this.downloadingOffers.clear();
    this.pendingAttachmentRemoval = null;
    this.removeAttachmentOpen = false;
    this.projectCreatedConfirmOpen = false;
    this.projectSyncConfirmOpen = false;
    this.projectSyncAccepted = false;
    this.saveScope = {kind: 'all'};
    this.pendingOfferRemoval = null;
    this.removeOfferOpen = false;
    this.resetExtendState();
    this.dialogEstimateSnapshot.clear();

    // NP20-774: Firmen mitladen, sonst kennt das Firmen-Badge beim direkten Aufruf der Detailseite
    // die Farbe und den Namen der Firma nicht — die Uebersicht laedt sie, dieser Weg nicht.
    forkJoin({
      companies: this.companyService.load(),
      entry: this.benchService.getEntry(id),
      sales: this.benchService.getSales(),
      businessUnits: this.benchService.getBusinessUnits().pipe(catchError(() => of([] as BusinessUnitDTO[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({entry, sales, businessUnits}) => {
          this.salesPersons = sales;
          this.salesIndex = buildSalesIndex(sales);
          this.businessUnits = businessUnits;
          this.buById = new Map(businessUnits.map(b => [b.id, b]));
          this.linkSubmittersByAbbreviation(entry);
          this.entry = entry;
          this.rememberSuggestedRates(entry);
          // Snapshot erst nach dem Nachziehen, sonst gilt der Eintrag sofort als geaendert.
          this.originalSnapshot = JSON.stringify(entry);
          this.rememberProjectCreatedOffers(entry);
          this.rememberProjectFields(entry);
          this.isLoading = false;
        },
        error: (err: unknown) => {
          this.isLoading = false;
          if (err instanceof HttpErrorResponse && err.status === 404) {
            this.messageService.error('benchDetail.notFound');
            this.returnToOrigin();
            return;
          }
          this.messageService.error('benchOverview.loadError');
        },
      });
  }

  /**
   * NP20-765: Altangebote hängen nur am gespeicherten Kürzel. Passt es noch zu einem Mitglied der
   * Sales-Liste, wird die Verknüpfung nachgezogen — danach übersteht die Zuordnung eine
   * Kürzel-Änderung. Wurde das Kürzel vorher schon geändert, ist die Zuordnung nicht mehr
   * herleitbar und die Person muss einmal neu gewählt werden.
   */
  private linkSubmittersByAbbreviation(entry: BenchEntryDTO): void {
    for (const offer of entry.offers ?? []) {
      if (!offer.submittedByUserId && offer.submittedBy) {
        offer.submittedByUserId = this.salesIndex.byAbbreviation.get(offer.submittedBy)?.userId ?? null;
      }
    }
  }

  // --- Dirty state & persistence -------------------------------------------

  get isDirty(): boolean {
    return !!this.entry && JSON.stringify(this.entry) !== this.originalSnapshot;
  }

  /** Grundstand als Objekt; neu gelesen nur, wenn sich der Snapshot geändert hat. */
  private get baseEntry(): BenchEntryDTO | null {
    this.refreshBaseCache();
    return this.baseCache.entry;
  }

  private refreshBaseCache(): void {
    if (this.baseCache.snapshot === this.originalSnapshot) return;
    const entry = this.originalSnapshot ? JSON.parse(this.originalSnapshot) as BenchEntryDTO : null;
    const offers = new Map((entry?.offers ?? [])
      .filter((o): o is OfferDTO & {id: string} => !!o.id)
      .map(o => [o.id, o] as const));
    this.baseCache = {snapshot: this.originalSnapshot, entry, offers};
  }

  /** NP20-1229: neu angelegt oder seit dem letzten Speichern geändert. */
  isOfferDirty(offer: OfferDTO): boolean {
    this.refreshBaseCache();
    return isOfferChanged(offer, this.baseCache.offers);
  }

  /** Andere Angebote spielen keine Rolle, nur die eigenen Pflichtangaben, laufendes Speichern oder ein Upload. */
  canSaveOffer(offer: OfferDTO): boolean {
    return !!this.entry && this.isOfferDirty(offer) && !this.isProbabilityMissing(offer)
      && !this.isOfferIncomplete(offer) && !this.hasInvalidEstimate(offer) && !this.isSaving && !this.hasBusyOffer;
  }

  offerSaveBlockedHint(offer: OfferDTO): string | null {
    if (this.isProbabilityMissing(offer)) return 'benchDetail.offers.saveBlockedProbability';
    if (this.isOfferIncomplete(offer)) return 'benchDetail.offers.saveBlockedIncomplete';
    return null;
  }

  /** Zugänglicher Name: „Angebot <Kunde> speichern“, sonst „Neues Angebot speichern“ bzw. der Knopftext. */
  offerSaveName(offer: OfferDTO): string {
    const customer = (offer.customer ?? '').trim();
    if (customer) return this.translateService.instant('benchDetail.offers.saveNamed', {customer});
    return this.translateService.instant(offer.id ? 'benchDetail.offers.save' : 'benchDetail.offers.saveNew');
  }

  get isNotesDirty(): boolean {
    return !!this.entry && JSON.stringify(this.entry.notes) !== JSON.stringify(this.baseEntry?.notes ?? []);
  }

  /** NP20-1229: Angebote spielen hier keine Rolle, nur laufendes Speichern oder ein Upload sperren. */
  get canSaveNotes(): boolean {
    return !!this.entry && this.isNotesDirty && !this.isSaving && !this.hasBusyOffer;
  }

  get isSaveAllDisabled(): boolean {
    return !this.canSave;
  }

  saveNotes(): void {
    if (!this.canSaveNotes) return;
    this.persistSection({kind: 'notes'}, (fresh, base, local) =>
      notesConflict(base.notes, local.notes, fresh.notes)
        ? {kind: 'conflict'}
        : {kind: 'put', payload: notesPayload(fresh, base.notes, local.notes)});
  }

  /**
   * NP20-1229: Derselbe Weg wie „Alles speichern“ (Rückfrage „Projekt angelegt“, dann „Projekt
   * nachziehen“), aber nur für dieses Angebot. `box` und `row` halten danach den Fokus im Angebot.
   */
  saveOffer(offer: OfferDTO, box?: HTMLElement, row?: HTMLElement): void {
    if (!this.canSaveOffer(offer)) return;
    this.saveScope = {kind: 'offer', offer};
    this.offerFocus = {box, row};
    this.startSave();
  }

  private offerFocus: {box?: HTMLElement; row?: HTMLElement} = {};

  private persistOffer(offer: OfferDTO): void {
    if (!this.entry?.offers.includes(offer) || !this.canSaveOffer(offer)) {
      this.saveScope = {kind: 'all'};
      return;
    }
    this.dropStaleExtendProject([offer]);
    const applyChanges = this.projectSyncAccepted && this.projectSyncOffers.includes(offer);
    this.saveScope = {kind: 'all'};
    this.projectSyncAccepted = false;
    this.persistSection({kind: 'offer', offer}, (fresh, base) => {
      // Alt-Angebote wie beim Laden verknüpfen, sonst gilt jedes als geändert; gesendet wird der Stand ohne.
      const linked = JSON.parse(JSON.stringify(fresh)) as BenchEntryDTO;
      this.linkSubmittersByAbbreviation(linked);
      return offerConflicts(offer, base.offers, linked.offers)
        ? {kind: 'conflict'}
        : {kind: 'put', payload: offerPayload(fresh, offer, applyChanges)};
    });
  }

  /** NP20-1229: Das Entfernen eines gespeicherten Angebots wird sofort gespeichert, ohne Konfliktprüfung. */
  private removeSavedOffer(offer: OfferDTO, offerId: string, box?: HTMLElement): void {
    if (!this.entry?.offers.includes(offer) || this.isSaving || this.hasBusyOffer) return;
    this.offerFocus = {box};
    this.persistSection({kind: 'remove', offer}, fresh =>
      fresh.offers.some(o => o.id === offerId)
        ? {kind: 'put', payload: withoutOffer(fresh, offerId)}
        : {kind: 'skip'});
  }

  /**
   * Einzel-Speichern: aktuellen Stand laden, nur den eigenen Bereich ersetzen, speichern. Meldet
   * `stepFor` einen Konflikt, hat jemand anderes den eigenen Bereich inzwischen geändert — dann wird
   * nicht gespeichert und das Formular bleibt, wie es ist. Anders als bei „Alles speichern“ wird bei
   * einem Konflikt nicht neu geladen, sonst gingen ungespeicherte Eingaben anderer Bereiche verloren.
   */
  private persistSection(
    section: SectionToSave,
    stepFor: (fresh: BenchEntryDTO, base: BenchEntryDTO, local: BenchEntryDTO) => SectionStep,
  ): void {
    const entryId = this.entry!.id;
    this.isSaving = true;
    this.benchService.getEntry(entryId)
      .pipe(
        switchMap((fresh): Observable<SectionSaveResult> => {
          const local = this.entry;
          const base = this.baseEntry;
          if (local?.id !== entryId || !base) return of({kind: 'stale'});
          const step = stepFor(fresh, base, local);
          if (step.kind === 'conflict') return of({kind: 'conflict'});
          if (step.kind === 'skip') return of({kind: 'saved', entry: fresh, fresh});
          return this.benchService.updateEntry(entryId, step.payload)
            .pipe(map((saved): SectionSaveResult => ({kind: 'saved', entry: saved, fresh})));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: result => {
          this.isSaving = false;
          if (this.entry?.id !== entryId) return;
          if (result.kind === 'conflict') this.messageService.error('benchDetail.sectionSaveConflict');
          if (result.kind === 'saved') this.adoptSection(section, result.entry, result.fresh);
        },
        error: (err: unknown) => {
          this.isSaving = false;
          if (this.entry?.id !== entryId) return;
          const conflict = err instanceof HttpErrorResponse && err.status === 409;
          this.messageService.error(conflict ? 'benchDetail.sectionSaveConflict' : 'benchDetail.saveError');
        },
      });
  }

  private adoptSection(section: SectionToSave, saved: BenchEntryDTO, fresh: BenchEntryDTO): void {
    const base = this.baseEntry;
    if (!this.entry || !base) return;
    this.linkSubmittersByAbbreviation(saved);
    const savedSection: SavedSection = section.kind === 'offer' ? {...section, fresh} : section;
    const adopted = adoptSectionResponse(this.entry, base, saved, savedSection);
    for (const offer of adopted.dropped) {
      this.expandedOffers.delete(offer);
      this.offersWithoutCostRate.delete(offer);
      this.extendChoices.delete(offer);
    }
    this.entry = adopted.entry;
    this.originalSnapshot = JSON.stringify(adopted.base);
    this.rememberProjectCreatedOffers(adopted.base);
    this.rememberProjectFields(adopted.base);
    if (section.kind === 'notes') {
      this.flashSaved('notes');
      this.keepFocusIn(this.notesSection?.nativeElement, this.notesTitle?.nativeElement);
      return;
    }
    const {box, row} = this.offerFocus;
    this.offerFocus = {};
    if (section.kind === 'offer') {
      this.offersWithoutCostRate.delete(section.offer);
      this.flashSaved(section.offer);
      this.resetExtendState();
      this.keepFocusIn(box, row);
    } else {
      this.flashSaved('offers');
      this.keepFocusIn(box, this.offersTitle?.nativeElement);
    }
  }

  /**
   * Der Speichern-Knopf ist danach gesperrt und verliert den Fokus. Er geht an die Überschrift bzw.
   * Zeile des Bereichs, außer der Fokus ist inzwischen woanders.
   */
  private keepFocusIn(section?: HTMLElement, target?: HTMLElement): void {
    const active = this.document.activeElement;
    const focusLost = !active || active === this.document.body;
    if (focusLost || section?.contains(active)) target?.focus({ preventScroll: true });
  }

  save(): void {
    if (!this.canSave) return;
    this.saveScope = {kind: 'all'};
    this.startSave();
  }

  private startSave(): void {
    const offers = this.newlyProjectCreatedOffers;
    if (offers.length > 0) {
      this.dialogEstimateSnapshot.clear();
      for (const offer of offers) this.dialogEstimateSnapshot.set(offer, takeEstimate(offer, DIALOG_KEYS));
      this.projectCreatedConfirmOpen = true;
      this.loadConsultantProjects();
      return;
    }
    this.saveAfterProjectCreated();
  }

  confirmProjectCreated(): void {
    if (this.projectCreatedSaveBlocked) return;
    this.projectCreatedConfirmOpen = false;
    for (const [offer, choice] of this.extendChoices) {
      if (choice.mode === 'extend') offer.extendedUntil = choice.newEnd;
    }
    this.dialogEstimateSnapshot.clear();
    this.saveAfterProjectCreated();
  }

  cancelProjectCreated(): void {
    this.projectCreatedConfirmOpen = false;
    for (const [offer, snapshot] of this.dialogEstimateSnapshot) applyEstimate(offer, snapshot, DIALOG_KEYS);
    this.dialogEstimateSnapshot.clear();
    this.resetExtendState();
    this.saveScope = {kind: 'all'};
  }

  /** NP20-1290: Der Hinweis richtet sich nach dem Stand beim Öffnen; beim Verlängern kommen Start und Ende aus dem Projekt. */
  isDialogPrefilled(offer: OfferDTO): boolean {
    const snapshot = this.dialogEstimateSnapshot.get(offer);
    if (!snapshot) return false;
    const values = this.isExtendMode(offer)
      ? [snapshot.volume, snapshot.volumeUnit]
      : [snapshot.projectStart, snapshot.projectEnd, snapshot.volume, snapshot.volumeUnit];
    return values.some(value => value != null && value !== '');
  }

  private saveAfterProjectCreated(): void {
    if (this.projectSyncOffers.length > 0) {
      this.projectSyncConfirmOpen = true;
      return;
    }
    this.persist();
  }

  confirmProjectSync(apply: boolean): void {
    this.projectSyncAccepted = apply;
    this.projectSyncConfirmOpen = false;
    this.persist();
  }

  cancelProjectSync(): void {
    this.projectSyncConfirmOpen = false;
    this.saveScope = {kind: 'all'};
  }

  /**
   * Nimmt den Statuswechsel entgegen und raeumt dabei sofort auf: wer ein Angebot wieder von
   * "Projekt angelegt" wegsetzt, verwirft die Eingaben dafuer. NP20-1290: Die vier Felder gehen
   * auf den zuletzt geladenen bzw. gespeicherten Stand des Angebots zurueck, nicht auf leer.
   */
  setOfferStatus(offer: OfferDTO, status: RequestStatus): void {
    const previous = offer.status;
    offer.status = status;
    const probability = PROBABILITY_BY_STATUS[status];
    if (previous !== status && probability != null) offer.probability = probability;
    if (previous === 'PROJECT_CREATED' && status !== previous && !offer.projectAutomationTriggered) {
      this.resetProjectEstimate(offer);
    }
    this.dropStaleExtendProject();
  }

  private resetProjectEstimate(offer: OfferDTO): void {
    this.refreshBaseCache();
    const base = offer.id ? this.baseCache.offers.get(offer.id) : undefined;
    applyEstimate(offer, base
      ? takeEstimate(base)
      : {projectStart: null, projectEnd: null, volume: null, volumeUnit: null});
  }

  /** Haengt am Status, nicht am Wert: ein Bestandsangebot mit 90 % bei "Offen" bleibt korrigierbar. */
  isProbabilityLocked(offer: OfferDTO): boolean {
    return STATUS_BOUND_PROBABILITY.has(offer.status);
  }

  isProbabilityMissing(offer: OfferDTO): boolean {
    return offer.probability == null;
  }

  /** 90 oder 100 ausserhalb ihres Status (aus einem frueheren Status oder Bestandsdaten). */
  showsProbabilityOutsideOptions(offer: OfferDTO): boolean {
    return !this.isProbabilityLocked(offer)
      && offer.probability != null
      && !this.probabilityOptions.includes(offer.probability);
  }

  get hasOfferWithoutProbability(): boolean {
    return this.entry?.offers.some(o => this.isProbabilityMissing(o)) ?? false;
  }

  /** NP20-1216: Rolle nach trim() nicht leer, beide Saetze groesser 0. */
  offerMissingFields(offer: OfferDTO): OfferRequiredField[] {
    const missing: OfferRequiredField[] = [];
    if (!offer.role?.trim()) missing.push('role');
    if (!this.isValidRate(offer.hourlyRateRemote)) missing.push('hourlyRateRemote');
    if (!this.isValidRate(offer.hourlyRateOnSite)) missing.push('hourlyRateOnSite');
    return missing;
  }

  isOfferIncomplete(offer: OfferDTO): boolean {
    return this.offerMissingFields(offer).length > 0;
  }

  isOfferFieldMissing(offer: OfferDTO, field: OfferRequiredField): boolean {
    return this.offerMissingFields(offer).includes(field);
  }

  get hasIncompleteOffer(): boolean {
    return this.entry?.offers.some(o => this.isOfferIncomplete(o)) ?? false;
  }

  private isValidRate(rate: number | null): boolean {
    return typeof rate === 'number' && rate > 0;
  }

  private get canSave(): boolean {
    return !!this.entry && this.isDirty && !this.isSaving && !this.hasBusyOffer && !this.hasOfferWithoutProbability
      && !this.hasIncompleteOffer && !this.entry.offers.some(o => this.hasInvalidEstimate(o));
  }

  private persist(): void {
    if (this.saveScope.kind === 'offer') {
      this.persistOffer(this.saveScope.offer);
      return;
    }
    if (!this.entry || !this.canSave) return;

    this.dropStaleExtendProject();
    this.isSaving = true;
    const payload = this.buildPayload();
    const entryId = this.entry.id;

    this.benchService.updateEntry(entryId, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: saved => {
          this.isSaving = false;
          if (saved) this.entry = saved;
          this.originalSnapshot = JSON.stringify(this.entry);
          this.rememberProjectCreatedOffers(this.entry);
          this.rememberProjectFields(this.entry);
          this.projectSyncAccepted = false;
          this.resetExtendState();
          this.flashSaved();
        },
        error: (err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 409) {
            this.messageService.error('benchDetail.saveConflict');
            this.benchService.getEntry(entryId)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: fresh => {
                  this.isSaving = false;
                  this.entry = fresh;
                  this.originalSnapshot = JSON.stringify(fresh);
                  this.rememberProjectCreatedOffers(fresh);
                  this.rememberProjectFields(fresh);
                  this.projectSyncAccepted = false;
                  this.resetExtendState();
                },
                error: () => {
                  this.isSaving = false;
                  this.messageService.error('benchDetail.saveError');
                },
              });
          } else {
            this.isSaving = false;
            this.messageService.error('benchDetail.saveError');
          }
        },
      });
  }

  /**
   * Der Verweis auf ein zu verlängerndes Projekt gilt nur für „Projekt angelegt“ mit Verlängerung.
   * NP20-1290: Zeitraum und Volumen bleiben dagegen bei jedem Status als Annahme am Angebot.
   */
  private dropStaleExtendProject(offers: OfferDTO[] = this.entry?.offers ?? []): void {
    for (const offer of offers) {
      if (offer.status !== 'PROJECT_CREATED' || !offer.extension) delete offer.extendProjectUuid;
    }
  }

  private rememberProjectCreatedOffers(entry: BenchEntryDTO | null): void {
    this.persistedProjectCreatedOfferIds = new Set(
      (entry?.offers ?? [])
        .filter((o): o is OfferDTO & { id: string } => !!o.id && o.status === 'PROJECT_CREATED')
        .map(o => o.id),
    );
  }

  private rememberProjectFields(entry: BenchEntryDTO | null): void {
    this.persistedProjectFields = new Map(
      (entry?.offers ?? [])
        .filter((o): o is OfferDTO & { id: string } => !!o.id)
        .map(o => [o.id, {role: o.role ?? '', customer: o.customer ?? ''}] as const),
    );
  }

  isNewlyProjectCreated(offer: OfferDTO): boolean {
    if (offer.projectAutomationTriggered) return false;
    return offer.status === 'PROJECT_CREATED' && !(offer.id && this.persistedProjectCreatedOfferIds.has(offer.id));
  }

  /** Bei „Angebot speichern“ nur dieses Angebot, sonst alle. */
  private get offersInSaveScope(): OfferDTO[] {
    if (this.saveScope.kind === 'offer') return [this.saveScope.offer];
    return this.entry?.offers ?? [];
  }

  get newlyProjectCreatedOffers(): OfferDTO[] {
    return this.offersInSaveScope.filter(o => this.isNewlyProjectCreated(o));
  }

  get hasLinkedProfile(): boolean {
    return !!this.entry?.consultantId;
  }

  changedProjectFields(offer: OfferDTO): ProjectFieldChange[] {
    const persisted = offer.id ? this.persistedProjectFields.get(offer.id) : undefined;
    if (!persisted) return [];
    const changes: ProjectFieldChange[] = [];
    if ((offer.role ?? '') !== persisted.role) {
      changes.push({labelKey: 'benchDetail.offers.field.role', from: persisted.role, to: offer.role ?? ''});
    }
    if ((offer.customer ?? '') !== persisted.customer) {
      changes.push({labelKey: 'benchDetail.offers.field.customer', from: persisted.customer, to: offer.customer ?? ''});
    }
    return changes;
  }

  get projectSyncOffers(): OfferDTO[] {
    if (!this.hasLinkedProfile) return [];
    return this.offersInSaveScope.filter(
      o => o.projectAutomationTriggered && this.changedProjectFields(o).length > 0,
    );
  }

  get extensionOffersInDialog(): OfferDTO[] {
    if (!this.hasLinkedProfile) return [];
    return this.newlyProjectCreatedOffers.filter(o => o.extension);
  }

  get isLoadingConsultantProjects(): boolean {
    return this.projectsLoadState === 'loading';
  }

  get isExtendModeFixed(): boolean {
    return this.projectsLoadState === 'error'
      || (this.projectsLoadState === 'loaded' && this.consultantProjects.length === 0);
  }

  get hasExtendModeOffer(): boolean {
    return this.newlyProjectCreatedOffers.some(o => this.isExtendMode(o));
  }

  get hasNewModeOffer(): boolean {
    return this.newlyProjectCreatedOffers.some(o => !this.isExtendMode(o));
  }

  projectCreatedMode(offer: OfferDTO): ProjectCreatedMode {
    return this.extendChoices.get(offer)?.mode ?? 'new';
  }

  isExtendMode(offer: OfferDTO): boolean {
    return this.projectCreatedMode(offer) === 'extend';
  }

  selectedExtendProject(offer: OfferDTO): IProjekt | null {
    return this.findProject(this.extendChoices.get(offer)?.projectUuid ?? null);
  }

  setProjectCreatedMode(offer: OfferDTO, mode: ProjectCreatedMode): void {
    const choice = this.extendChoices.get(offer);
    if (!choice || choice.mode === mode || this.isLoadingConsultantProjects) return;
    if (mode === 'extend' && this.isExtendModeFixed) return;
    if (mode === 'extend') {
      choice.newStart = offer.projectStart ?? null;
      choice.newModeEnd = offer.projectEnd ?? null;
    }
    choice.mode = mode;
    this.applyChoice(offer);
  }

  selectExtendProject(offer: OfferDTO, uuid: string | null): void {
    const choice = this.extendChoices.get(offer);
    if (!choice) return;
    choice.projectUuid = uuid;
    this.applyChoice(offer);
  }

  setExtendNewEnd(offer: OfferDTO, iso: string | null): void {
    const choice = this.extendChoices.get(offer);
    if (!choice) return;
    choice.newEnd = iso;
    this.applyChoice(offer);
  }

  isExtendProjectMissing(offer: OfferDTO): boolean {
    return this.isExtendMode(offer) && this.projectsLoadState === 'loaded' && !offer.extendProjectUuid;
  }

  isExtendEndNotAfterPrevious(offer: OfferDTO): boolean {
    if (!this.isExtendMode(offer) || !offer.projectEnd) return false;
    const previousEnd = isoDay(this.selectedExtendProject(offer)?.ende);
    return !!previousEnd && offer.projectEnd <= previousEnd;
  }

  extendMinDate(offer: OfferDTO): Moment | null {
    const project = this.selectedExtendProject(offer);
    const previousEnd = isoDay(project?.ende);
    if (previousEnd) return this.toMoment(moment(previousEnd, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD'));
    return this.toMoment(isoDay(project?.beginn));
  }

  private loadConsultantProjects(): void {
    this.resetExtendState();
    const consultantId = this.entry?.consultantId;
    const offers = this.extensionOffersInDialog;
    if (!consultantId || offers.length === 0) return;

    for (const offer of offers) {
      const fromNewMode = !('extendProjectUuid' in offer);
      this.extendChoices.set(offer, {
        mode: 'extend',
        projectUuid: null,
        newEnd: null,
        newStart: fromNewMode ? offer.projectStart ?? null : null,
        newModeEnd: fromNewMode ? offer.projectEnd ?? null : null,
      });
    }
    this.projectsLoadState = 'loading';
    this.projectsSubscription = this.beraterService.listProjects(consultantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: projects => {
          this.consultantProjects = (projects ?? []).filter(p => !!p.uuid && !isDraft(p));
          this.projectsLoadState = 'loaded';
          this.initExtendChoices();
        },
        error: () => {
          this.projectsLoadState = 'error';
          this.initExtendChoices();
        },
      });
  }

  private initExtendChoices(): void {
    for (const [offer, choice] of this.extendChoices) {
      if (this.isExtendModeFixed) {
        choice.mode = 'new';
      } else {
        choice.projectUuid = this.preselectProject(offer)?.uuid ?? null;
        choice.newEnd = offer.extendedUntil ?? null;
      }
      this.applyChoice(offer);
    }
  }

  private preselectProject(offer: OfferDTO): IProjekt | null {
    const customer = normalizedCustomer(offer.customer);
    if (!customer) return null;
    const endKey = (p: IProjekt): string => isoDay(p.ende) ?? '9999-12-31';
    const startKey = (p: IProjekt): string => isoDay(p.beginn) ?? '';
    return this.consultantProjects
      .filter(p => normalizedCustomer(p.kunde) === customer)
      .sort((a, b) => endKey(b).localeCompare(endKey(a)) || startKey(b).localeCompare(startKey(a)))[0] ?? null;
  }

  private findProject(uuid: string | null): IProjekt | null {
    if (!uuid) return null;
    return this.consultantProjects.find(p => p.uuid === uuid) ?? null;
  }

  private applyChoice(offer: OfferDTO): void {
    const choice = this.extendChoices.get(offer);
    if (!choice) return;
    if (choice.mode === 'extend') {
      if (choice.projectUuid) offer.extendProjectUuid = choice.projectUuid;
      else delete offer.extendProjectUuid;
      offer.projectStart = isoDay(this.findProject(choice.projectUuid)?.beginn);
      offer.projectEnd = choice.newEnd;
    } else {
      delete offer.extendProjectUuid;
      offer.projectStart = choice.newStart;
      offer.projectEnd = choice.newModeEnd;
    }
  }

  private restoreNewModeDates(offer: OfferDTO): void {
    const choice = this.extendChoices.get(offer);
    if (choice?.mode !== 'extend') return;
    delete offer.extendProjectUuid;
    offer.projectStart = choice.newStart;
    offer.projectEnd = choice.newModeEnd;
  }

  private resetExtendState(): void {
    this.projectsSubscription?.unsubscribe();
    this.projectsSubscription = null;
    this.extendChoices.clear();
    this.consultantProjects = [];
    this.projectsLoadState = 'idle';
  }

  isProjectDataMissing(offer: OfferDTO): boolean {
    return !offer.projectStart || !offer.projectEnd || offer.volume == null || !offer.volumeUnit;
  }

  isProjectRangeInvalid(offer: OfferDTO): boolean {
    return !!offer.projectStart && !!offer.projectEnd && offer.projectEnd < offer.projectStart;
  }

  /** Grenzen des Backends: positiv, hoechstens sechs Vorkomma- und zwei Nachkommastellen. */
  isVolumeInvalid(offer: OfferDTO): boolean {
    const volume = offer.volume;
    if (volume == null) return false;
    return volume <= 0 || volume >= 1_000_000 || Math.abs(Math.round(volume * 100) - volume * 100) > 1e-6;
  }

  /** NP20-1290: Nach gelaufener Automatik zeigt das Angebot den Zeitraum nur noch lesend. */
  isEstimateEditable(offer: OfferDTO): boolean {
    return !offer.projectAutomationTriggered;
  }

  hasInvalidEstimate(offer: OfferDTO): boolean {
    return this.isEstimateEditable(offer) && (this.isProjectRangeInvalid(offer) || this.isVolumeInvalid(offer));
  }

  /** Lesende Anzeige ohne Locale-Daten (nur „en“ ist geladen): de TT.MM.JJJJ, en MM/DD/YYYY. */
  estimateDateLabel(iso: string | null | undefined): string {
    if (!iso) return '—';
    return moment(iso, 'YYYY-MM-DD').format(this.isEnglishUi ? 'MM/DD/YYYY' : 'DD.MM.YYYY');
  }

  estimateVolumeLabel(offer: OfferDTO): string {
    if (offer.volume == null) return '—';
    const volume = this.isEnglishUi ? String(offer.volume) : String(offer.volume).replace('.', ',');
    if (!offer.volumeUnit) return volume;
    const unit = this.translateService.instant(`benchDetail.offers.projectCreated.unit.${offer.volumeUnit.toLowerCase()}`);
    return `${volume} ${unit}`;
  }

  private get isEnglishUi(): boolean {
    return isEnglish(this.translateService.currentLang);
  }

  /**
   * Ohne Berater-Profil legt das Backend kein Projekt an — dann gibt es nichts zu erfragen und
   * nichts zu blockieren. Zeitraum und Volumen speichert es trotzdem als Annahme am Angebot.
   */
  get projectCreatedSaveBlocked(): boolean {
    if (!this.hasLinkedProfile) return false;
    if (this.isLoadingConsultantProjects) return true;
    return this.newlyProjectCreatedOffers.some(
      o => this.isProjectDataMissing(o) || this.isProjectRangeInvalid(o) || this.isVolumeInvalid(o)
        || this.isExtendProjectMissing(o) || this.isExtendEndNotAfterPrevious(o),
    );
  }

  goToProfile(): void {
    if (this.entry?.consultantId) {
      this.router.navigate(['/profile', this.entry.consultantId]);
    }
  }

  /** Sprung-Anker (NP20-795): scrollt zur Bemerkungen-Sektion. */
  scrollToNotes(): void {
    this.notesSection?.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start'});
  }

  // „€/h" ist ein international gebräuchliches Einheitensymbol (Währungszeichen + Zeiteinheit)
  // und wird bewusst nicht über i18n lokalisiert.
  /** Konditionen kompakt für die zugeklappte Angebotszeile: „95 / 110 €/h". */
  firmColor(abbreviation: string | null | undefined): string | null {
    return this.companyService.byAbbreviation(abbreviation ?? undefined)?.color ?? null;
  }

  buFor(offer: OfferDTO): BusinessUnitDTO | null {
    return (offer.businessUnitId && this.buById.get(offer.businessUnitId)) || null;
  }

  buOptions(offer: OfferDTO): BusinessUnitDTO[] {
    return this.businessUnits.filter(b => b.active || b.id === offer.businessUnitId);
  }

  conditionsLabel(offer: OfferDTO): string {
    const remote = offer.hourlyRateRemote;
    const onSite = offer.hourlyRateOnSite;
    if (!remote && !onSite) return '';
    if (remote && onSite) return `${remote} / ${onSite} €/h`;
    return `${remote || onSite} €/h`;
  }

  backToList(): void {
    this.returnToOrigin();
  }

  /** Aus der Sales-Ansicht geöffnet (`from=sales`) zurück dorthin, mit deren Filtern; sonst zur Übersicht (NP20-1283). */
  private returnToOrigin(): void {
    const params = this.route.snapshot.queryParamMap;
    if (params.get('from') === 'sales') {
      this.router.navigate(['/berater-abfrage/sales'], {queryParams: salesParamsOf(params)});
      return;
    }
    this.router.navigate(['/berater-abfrage']);
  }

  // --- Hero header / sales --------------------------------------------------

  getInitials(fullName: string): string {
    if (!fullName) return '';
    return fullName
      .split(' ')
      .map(w => w[0] ?? '')
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  /**
   * NP20-924: Wer betreut das Angebot gerade? Liefert das Kürzel der anbietenden Person, während
   * einer Abwesenheit zusätzlich das des Vertreters — dieselbe Ableitung wie in der Sales-View und
   * im Sales-Filter der Übersicht.
   *
   * NP20-765: Das Kürzel kommt über die verknüpfte Person, nicht über den gespeicherten Wert. Nach
   * einer Kürzel-Änderung in der Benutzerverwaltung erscheint damit der neue Wert statt einer Lücke;
   * der gespeicherte Wert bleibt Rückfall für Altangebote ohne Verknüpfung.
   */
  submitter(offer: OfferDTO): SalesSubstitution {
    return this.substitution.resolve(offer, this.salesIndex);
  }

  /**
   * Die Sales-Liste plus die am Angebot hinterlegte Person, falls sie nicht (mehr) darin steht —
   * sonst zeigt die Angebotszeile ihr Kürzel und das Formular darunter nichts. Wie bei den
   * Business Units, wo eine deaktivierte Einheit an ihrem Angebot wählbar bleibt.
   */
  submitterOptions(offer: OfferDTO): SubmitterOption[] {
    const options = this.salesPersons.map(s => ({userId: s.userId, abbreviation: s.abbreviation}));
    const stored = this.submitter(offer).original;
    if (offer.submittedByUserId && stored && !this.salesIndex.byUserId.has(offer.submittedByUserId)) {
      options.push({userId: offer.submittedByUserId, abbreviation: stored});
    }
    return options;
  }

  setSubmitter(offer: OfferDTO, userId: string | null): void {
    if (!userId) {
      offer.submittedByUserId = null;
      offer.submittedBy = '';
      return;
    }
    // Wer nicht mehr in der Sales-Liste steht, hat dort kein Kürzel mehr; das am Angebot
    // gespeicherte bleibt dann stehen, solange die Auswahl auf dieser Person bleibt.
    const listed = this.salesIndex.byUserId.get(userId)?.abbreviation;
    offer.submittedBy = listed ?? (userId === offer.submittedByUserId ? offer.submittedBy : '');
    offer.submittedByUserId = userId;
  }

  // --- Offers ---------------------------------------------------------------

  offerCount(status: RequestStatus): number {
    return this.entry?.offers.filter(o => o.status === status).length ?? 0;
  }

  get pdlInterviewCount(): number {
    return this.entry?.offers.filter(o => o.interviewPdl === 'ja').length ?? 0;
  }

  get customerInterviewCount(): number {
    return this.entry?.offers.filter(o => o.interviewCustomer === 'ja').length ?? 0;
  }

  get visibleOffers(): OfferDTO[] {
    let list = this.entry?.offers ? [...this.entry.offers] : [];

    const term = this.offerSearch.trim().toLowerCase();
    if (term) {
      // NP20-867: die externe Ref-Nr. mitsuchen, wenn sie gesetzt ist — die Sales-Ansicht tut das schon.
      list = list.filter(o =>
        o.customer?.toLowerCase().includes(term) ||
        o.role?.toLowerCase().includes(term) ||
        o.referenceNumber?.toLowerCase().includes(term) ||
        o.externalReferenceNumber?.toLowerCase().includes(term),
      );
    }

    if (this.offerStatusFilter !== 'ALL') {
      list = list.filter(o => o.status === this.offerStatusFilter);
    }

    list.sort((a, b) => {
      const cmp = (a.date ?? '').localeCompare(b.date ?? '');
      return this.offerNewestFirst ? -cmp : cmp;
    });
    return list;
  }

  isOfferExpanded(offer: OfferDTO): boolean {
    return this.expandedOffers.has(offer);
  }

  /**
   * NP20-934: Nur am frisch angelegten Angebot und nur, wenn nichts vorbelegt
   * werden konnte — an gespeicherten Angeboten sagt ein leerer Satz nichts ueber den
   * Kostensatz aus. Der Hinweis haengt am einzelnen Angebot: wird der Kostensatz
   * zwischendurch gepflegt, erklaert er die leeren Felder der vorher angelegten weiterhin.
   */
  showMissingCostHint(offer: OfferDTO): boolean {
    return this.offersWithoutCostRate.has(offer);
  }

  /** NP20-1236: Die Projektverlängerung ist nur vor dem ersten Speichern setzbar. */
  isExtensionLocked(offer: OfferDTO): boolean {
    return !!offer.id;
  }

  /** NP20-1290: „Verlängert bis“ bleibt änderbar, bis die Automatik für das Angebot gelaufen ist. */
  isExtendedUntilLocked(offer: OfferDTO): boolean {
    return !!offer.projectAutomationTriggered;
  }

  /** NP20-1238: Ausschalten leert das Datum, damit es mit dem nächsten Speichern auch serverseitig weg ist. */
  toggleExtension(offer: OfferDTO): void {
    if (this.isExtensionLocked(offer)) return;
    offer.extension = !offer.extension;
    if (!offer.extension) {
      offer.extendedUntil = null;
      this.restoreNewModeDates(offer);
      delete offer.extendProjectUuid;
      this.extendChoices.delete(offer);
    }
  }

  toggleOffer(offer: OfferDTO): void {
    if (this.expandedOffers.has(offer)) {
      this.expandedOffers.delete(offer);
    } else {
      this.expandedOffers.add(offer);
    }
  }

  collapseOffer(offer: OfferDTO): void {
    this.expandedOffers.delete(offer);
  }

  /**
   * NP20-934: Liest die Saetze erst frisch. Wird der Kostensatz woanders gepflegt — zweiter Tab,
   * Administration —, arbeitet eine offene Detailseite sonst bis zum Neuladen mit dem Stand vom
   * Seitenaufruf und legt das Angebot mit dem alten Wert an.
   */
  addOffer(): void {
    if (!this.entry || this.isAddingOffer) return;
    const id = this.entry.id;
    this.isAddingOffer = true;
    this.benchService.getEntry(id)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isAddingOffer = false))
      .subscribe({
        // Nur die beiden Zahlen uebernehmen: die Antwort traegt auch Angebote und Bemerkungen,
        // und die sind hier aelter als der noch nicht gespeicherte Stand auf dem Bildschirm.
        next: fresh => {
          if (this.entry?.id !== id) return;
          this.rememberSuggestedRates(fresh);
          this.insertOffer();
        },
        // Am Nachlesen darf das Anlegen nicht scheitern; dann gilt der Stand vom Seitenaufruf.
        error: () => {
          if (this.entry?.id === id) this.insertOffer();
        },
      });
  }

  private rememberSuggestedRates(entry: BenchEntryDTO): void {
    this.suggestedRates = {remote: entry.suggestedHourlyRateRemote, onSite: entry.suggestedHourlyRateOnSite};
  }

  private insertOffer(): void {
    if (!this.entry) return;
    const offer: OfferDTO = {
      // Die id vergibt der Server beim Speichern; bis dahin gibt es kein Angebot,
      // an das ein Anhang gehaengt werden koennte.
      id: null,
      date: new Date().toISOString().split('T')[0],
      // NP20-924: keine Vorbelegung. Vorbelegt wurde der Betreuer des Eintrags — den gibt es nicht
      // mehr. Die anbietende Person wird bewusst aus dem Dropdown gewaehlt.
      submittedBy: '',
      submittedByUserId: null,
      referenceNumber: this.nextReferenceNumber(),
      externalReferenceNumber: null,
      role: '',
      customer: '',
      intermediary: '',
      businessUnitId: null,
      status: 'OPEN',
      priority: 'MEDIUM',
      probability: PROBABILITY_BY_STATUS.OPEN ?? null,
      commitmentFrom: null,
      extension: false,
      extendedUntil: null,
      projectStart: null,
      projectEnd: null,
      volume: null,
      volumeUnit: null,
      interviewPdl: '',
      interviewCustomer: '',
      // NP20-934: Vorbelegung aus Kostensatz und den zentralen Parametern, vom Backend
      // gerechnet (NP20-946). Ohne gepflegten Kostensatz bleiben die Felder leer statt einer
      // Summe aus blossen Zuschlaegen; der Hinweis am Angebot sagt, warum.
      hourlyRateRemote: this.suggestedRates.remote ?? null,
      hourlyRateOnSite: this.suggestedRates.onSite ?? null,
      hubspotUrl: '',
      notes: [],
      attachment: null,
    };
    this.entry.offers = [...this.entry.offers, offer];
    if (this.suggestedRates.remote == null) this.offersWithoutCostRate.add(offer);
    this.expandedOffers.add(offer);
    // NP20-867: Ein neues Angebot ist noch leer und faellt durch jede Suche und jeden
    // Statusfilter — beides zuruecksetzen, sonst legt man es an und sieht es nicht.
    this.offerSearch = '';
    this.offerStatusFilter = 'ALL';
  }

  private pendingOfferRemoval: OfferDTO | null = null;
  removeOfferOpen = false;

  /** `box` umschließt das Angebot; liegt der Fokus nach dem Entfernen noch darin, geht er an die Überschrift. */
  private pendingOfferRemovalBox: HTMLElement | undefined;

  askRemoveOffer(offer: OfferDTO, box?: HTMLElement): void {
    if (!this.entry) return;
    this.pendingOfferRemoval = offer;
    this.pendingOfferRemovalBox = box;
    this.removeOfferOpen = true;
  }

  cancelRemoveOffer(): void {
    this.pendingOfferRemoval = null;
    this.pendingOfferRemovalBox = undefined;
    this.removeOfferOpen = false;
  }

  /** NP20-1229: Ein gespeichertes Angebot wird sofort auf dem Server entfernt, ein neues nur im Formular. */
  confirmRemoveOffer(): void {
    const offer = this.pendingOfferRemoval;
    const box = this.pendingOfferRemovalBox;
    this.cancelRemoveOffer();
    if (!offer) return;
    if (offer.id) this.removeSavedOffer(offer, offer.id, box);
    else this.removeOffer(offer);
  }

  get pendingOfferRemovalHint(): string {
    return this.pendingOfferRemoval?.id
      ? 'benchDetail.offers.removeConfirm.hint'
      : 'benchDetail.offers.removeConfirm.hintUnsaved';
  }

  get pendingOfferRemovalLabel(): string {
    const offer = this.pendingOfferRemoval;
    if (!offer) return '';
    return [offer.referenceNumber, offer.role, offer.customer]
      .map(part => (part ?? '').trim())
      .find(part => part.length > 0) ?? '';
  }

  removeOffer(offer: OfferDTO): void {
    if (!this.entry || !this.entry.offers.includes(offer)) return;
    this.entry.offers = this.entry.offers.filter(o => o !== offer);
    this.expandedOffers.delete(offer);
  }

  copyText(value: string | null): void {
    if (!value) {
      return;
    }
    navigator.clipboard?.writeText(value).then(
      () => this.messageService.success('benchDetail.offers.refCopied'),
      () => { /* clipboard unavailable — silently ignore */ },
    );
  }

  openHubspot(url: string): void {
    if (!url) return;
    // Without a protocol the browser treats the value as relative (-> localhost/<url>)
    const target = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    window.open(target, '_blank', 'noopener');
  }

  /**
   * NP20-765: Firma und Kürzel können beide fehlen. Vorher wurde die Firma ungeprüft interpoliert,
   * deshalb begann die interne Ref-Nr. mit „null". Jetzt zählen nur vorhandene Teile, und ohne
   * beide bleibt die Eintragsnummer als eindeutiger Stamm.
   */
  private referencePrefix(entry: BenchEntryDTO): string {
    const parts = [entry.firm, entry.abbreviation]
      .map(part => (part ?? '').trim())
      .filter(part => part.length > 0);
    return parts.length ? parts.join('-') : String(entry.number);
  }

  private nextReferenceNumber(): string {
    if (!this.entry) return '';
    const prefix = this.referencePrefix(this.entry);
    const existing = new Set(this.entry.offers.map(o => o.referenceNumber));
    const maxSeq = this.entry.offers.reduce((max, o) => {
      const match = /(\d+)$/.exec(o.referenceNumber ?? '');
      return match ? Math.max(max, parseInt(match[1], 10)) : max;
    }, 0);
    // maxSeq ignores refs without a numeric suffix (e.g. a manual "TSB-MM-alt"),
    // so guard against colliding with any existing ref and bump until unique.
    let seq = maxSeq + 1;
    let candidate = `${prefix}-${String(seq).padStart(3, '0')}`;
    while (existing.has(candidate)) {
      candidate = `${prefix}-${String(++seq).padStart(3, '0')}`;
    }
    return candidate;
  }

  // --- Notes ----------------------------------------------------------------

  addNote(): void {
    if (!this.entry) return;
    this.entry.notes = [...this.entry.notes, this.createNote()];
  }

  /** `offer` trennt die Bemerkung am Angebot von der am Eintrag — beide teilen sich die Rueckfrage. */
  private pendingNoteRemoval: {offer: OfferDTO | null; note: NoteDTO} | null = null;
  removeNoteOpen = false;

  askRemoveNote(note: NoteDTO): void {
    this.pendingNoteRemoval = {offer: null, note};
    this.removeNoteOpen = true;
  }

  askRemoveOfferNote(offer: OfferDTO, note: NoteDTO): void {
    this.pendingNoteRemoval = {offer, note};
    this.removeNoteOpen = true;
  }

  cancelRemoveNote(): void {
    this.pendingNoteRemoval = null;
    this.removeNoteOpen = false;
  }

  confirmRemoveNote(): void {
    const pending = this.pendingNoteRemoval;
    this.pendingNoteRemoval = null;
    this.removeNoteOpen = false;
    if (!pending) return;
    if (pending.offer) {
      this.removeOfferNote(pending.offer, pending.note);
    } else {
      this.removeNote(pending.note);
    }
  }

  /** NP20-1229: Eine Bemerkung am Angebot wird von „Bemerkungen speichern“ nicht erfasst. */
  get pendingNoteRemovalHint(): string {
    return this.pendingNoteRemoval?.offer
      ? 'benchDetail.notes.removeConfirm.hintOffer'
      : 'benchDetail.notes.removeConfirm.hint';
  }

  get pendingNoteRemovalLabel(): string {
    return (this.pendingNoteRemoval?.note.text ?? '').trim();
  }

  removeNote(note: NoteDTO): void {
    if (!this.entry || !this.entry.notes.includes(note)) return;
    this.entry.notes = this.entry.notes.filter(n => n !== note);
  }

  onNoteKeydown(event: KeyboardEvent, note: NoteDTO): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.addNote();
    } else if (event.key === 'Delete' && !(note.text ?? '').trim()) {
      event.preventDefault();
      this.askRemoveNote(note);
    }
  }

  addOfferNote(offer: OfferDTO): void {
    offer.notes = [...offer.notes, this.createNote()];
  }

  removeOfferNote(offer: OfferDTO, note: NoteDTO): void {
    offer.notes = offer.notes.filter(n => n !== note);
  }

  onOfferNoteKeydown(event: KeyboardEvent, offer: OfferDTO, note: NoteDTO): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.addOfferNote(offer);
    } else if (event.key === 'Delete' && !(note.text ?? '').trim()) {
      event.preventDefault();
      this.askRemoveOfferNote(offer, note);
    }
  }

  private createNote(): NoteDTO {
    // Neue Notiz ohne id und ohne Autor — das Backend vergibt beides (Autor = Kürzel des eingeloggten Users).
    return {
      id: null,
      date: new Date().toISOString().split('T')[0],
      author: '',
      text: '',
      isMeetingNote: false,
    };
  }

  // --- Date conversion (DTO stores ISO strings, datepicker uses Moment) -----

  private readonly momentCache = new Map<string, Moment>();

  /**
   * ISO date string -> Moment for the datepicker. Cached so the same string
   * yields the same instance, keeping the binding reference stable across
   * change detection (no churn, inputs stay editable).
   */
  toMoment(value: string | null): Moment | null {
    if (!value) return null;
    let m = this.momentCache.get(value);
    if (!m) {
      m = moment(value, 'YYYY-MM-DD');
      this.momentCache.set(value, m);
    }
    return m;
  }

  /** Datepicker Moment (or null when cleared) -> ISO date string for the DTO. */
  toIso(value: Moment | null): string | null {
    return this.dateService.toLocalDateString(value);
  }

  // --- Misc -----------------------------------------------------------------

  // --- Datei-Anhang am Angebot ---------------------------------------------

  /**
   * Angebots-Ids, deren Anhang gerade geschrieben wird (Upload, Entfernen). Ueber die Id und
   * nicht ueber das Objekt: ein einzelnes Feld liesse parallele Uploads einander
   * freigeben, und eine Objekt-Referenz uebersteht das Speichern nicht — `save()` und
   * `loadEntry()` tauschen alle Angebots-Objekte aus, die Sperre gilt aber weiter.
   */
  private readonly uploadingOffers = new Set<string>();

  /**
   * Laufende Downloads. Getrennt vom Schreiben, weil ein Download den Eintrag auf dem
   * Server nicht anfasst und das Speichern deshalb nicht sperren darf.
   */
  private readonly downloadingOffers = new Set<string>();

  /** Angebot, dessen Anhang nach Rueckfrage entfernt werden soll. */
  private pendingAttachmentRemoval: OfferDTO | null = null;
  removeAttachmentOpen = false;

  isBusy(offer: OfferDTO): boolean {
    return !!offer.id && (this.uploadingOffers.has(offer.id) || this.downloadingOffers.has(offer.id));
  }

  /**
   * Sperrt das Schreiben am Anhang, solange der Eintrag gespeichert wird: das PUT traegt
   * den Angebots-Stand von vorher, seine Antwort ersetzt alle Angebots-Objekte und wuerde
   * einen zwischenzeitlich angehaengten Anhang wieder aus der Anzeige nehmen. Das Gegenstueck
   * zu `hasBusyOffer`. Herunterladen bleibt frei, es fasst den Eintrag nicht an.
   */
  isWriteLocked(offer: OfferDTO): boolean {
    return this.isSaving || this.isBusy(offer);
  }

  /** Sperrt das Speichern, solange geschrieben wird: der Eintrag darf nicht darunter wegwandern. */
  get hasBusyOffer(): boolean {
    return this.uploadingOffers.size > 0;
  }

  canAttach(offer: OfferDTO): boolean {
    return !!offer.id;
  }

  attachmentSize(offer: OfferDTO): string {
    return formatFileSize(offer.attachment?.sizeBytes ?? null, this.translateService.currentLang);
  }

  attachmentIcon(offer: OfferDTO): string {
    return fileIcon(offer.attachment?.fileName);
  }

  attachmentKind(offer: OfferDTO): string {
    return fileKind(offer.attachment?.fileName);
  }

  onAttachmentSelected(offer: OfferDTO, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    // Der Datei-Dialog liefert dieselbe Datei sonst kein zweites Mal.
    input.value = '';
    if (!file || !this.entry || !offer.id || this.isWriteLocked(offer)) return;

    if (file.size === 0) {
      this.messageService.error('benchDetail.offers.attachment.errorEmpty');
      return;
    }
    if (file.size > OFFER_ATTACHMENT_MAX_BYTES) {
      this.messageService.error('benchDetail.offers.attachment.errorTooLarge');
      return;
    }

    const entryId = this.entry.id;
    const offerId = offer.id;
    let settled = false;
    this.uploadingOffers.add(offerId);
    this.benchService.uploadOfferAttachment(entryId, offerId, file, offer.attachment?.note ?? null)
      .pipe(
        finalize(() => {
          this.uploadingOffers.delete(offerId);
          // Weder Antwort noch Fehler: das Abo endete vorzeitig, weil die Seite verlassen
          // wurde. Ohne Meldung haelt der Benutzer die Datei fuer angehaengt.
          if (!settled) this.messageService.warning('benchDetail.offers.attachment.aborted');
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: saved => {
          settled = true;
          const stored = saved?.offers?.find(o => o.id === offerId)?.attachment ?? null;
          // Kein Anhang in der Antwort heisst nicht "es gibt keinen": die Datei liegt auf
          // dem Server. Kachel und Vergleichsstand bleiben unangetastet — ebenso, wenn die
          // Antwort nirgends mehr ankommt, weil inzwischen ein anderer Eintrag geladen wurde.
          if (!stored || !this.applyAttachment(entryId, offerId, stored)) {
            this.messageService.error('benchDetail.offers.attachment.errorStale');
            return;
          }
          this.messageService.success('benchDetail.offers.attachment.uploaded');
        },
        error: (err: unknown) => {
          settled = true;
          this.messageService.error(this.uploadErrorKey(err));
        },
      });
  }

  /**
   * Als Blob mit fest verdrahtetem `application/octet-stream`. Der gespeicherte
   * Inhaltstyp darf hier nicht landen: die Kategorie laesst jeden Dateityp zu, und
   * eine als HTML geoeffnete Datei liefe im Origin der Anwendung.
   */
  downloadAttachment(offer: OfferDTO): void {
    if (!this.entry || !offer.id || !offer.attachment) return;

    const offerId = offer.id;
    this.downloadingOffers.add(offerId);
    this.benchService.downloadOfferAttachment(this.entry.id, offerId)
      .pipe(
        finalize(() => this.downloadingOffers.delete(offerId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => this.saveAttachment(response.body, response.headers.get('Content-Disposition'), offer),
        error: () => this.messageService.error('benchDetail.offers.attachment.errorDownload'),
      });
  }

  /** Loescht Datei und Notiz beim Server — dafuer gibt es die Rueckfrage. */
  askRemoveAttachment(offer: OfferDTO): void {
    if (!offer.attachment) return;
    this.pendingAttachmentRemoval = offer;
    this.removeAttachmentOpen = true;
  }

  cancelRemoveAttachment(): void {
    this.pendingAttachmentRemoval = null;
    this.removeAttachmentOpen = false;
  }

  confirmRemoveAttachment(): void {
    const offer = this.pendingAttachmentRemoval;
    this.pendingAttachmentRemoval = null;
    this.removeAttachmentOpen = false;
    if (offer) this.removeAttachment(offer);
  }

  removeAttachment(offer: OfferDTO): void {
    if (!this.entry || !offer.id || !offer.attachment || this.isWriteLocked(offer)) return;

    const entryId = this.entry.id;
    const offerId = offer.id;
    this.uploadingOffers.add(offerId);
    this.benchService.deleteOfferAttachment(entryId, offerId)
      .pipe(
        finalize(() => this.uploadingOffers.delete(offerId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          if (!this.applyAttachment(entryId, offerId, null)) {
            this.messageService.error('benchDetail.offers.attachment.errorStaleDelete');
            return;
          }
          this.messageService.success('benchDetail.offers.attachment.deleted');
        },
        error: () => this.messageService.error('benchDetail.offers.attachment.errorDelete'),
      });
  }

  private saveAttachment(body: Blob | null, disposition: string | null, offer: OfferDTO): void {
    if (!body) {
      this.messageService.error('benchDetail.offers.attachment.errorDownload');
      return;
    }
    const name = fileNameFromContentDisposition(disposition)
      ?? sanitizeFileName(offer.attachment?.fileName)
      ?? 'attachment';
    saveBlob(new Blob([body], {type: 'application/octet-stream'}), name);
  }

  /**
   * Der Anhang ist mit der Antwort bereits gespeichert. Er wandert deshalb auch in
   * den Vergleichsstand, sonst meldet die Seite ungespeicherte Aenderungen fuer
   * etwas, das schon auf dem Server liegt.
   *
   * Gesucht wird am aktuellen Eintrag statt auf der Referenz von damals: waehrend des
   * Laufs koennen `save()` oder ein Neuladen alle Angebots-Objekte ausgetauscht haben,
   * ein Schreiben auf das abgehaengte Objekt bliebe unsichtbar. Gehoert die Antwort zu
   * einem anderen Eintrag, wird sie verworfen.
   *
   * `false` heisst: die Antwort ist nirgends angekommen. Der Aufrufer haengt seine
   * Meldung daran, sonst meldet er Erfolg fuer eine Kachel, die es nicht mehr gibt.
   */
  private applyAttachment(entryId: string, offerId: string, attachment: OfferAttachmentDTO | null): boolean {
    if (this.entry?.id !== entryId) return false;
    const offer = this.entry.offers?.find(o => o.id === offerId);
    if (!offer) return false;

    offer.attachment = attachment;
    const snapshot = JSON.parse(this.originalSnapshot) as BenchEntryDTO;
    const snapshotOffer = snapshot.offers?.find(o => o.id === offerId);
    if (snapshotOffer) {
      snapshotOffer.attachment = attachment ? {...attachment} : null;
      this.originalSnapshot = JSON.stringify(snapshot);
    }
    return true;
  }

  private uploadErrorKey(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 413) return 'benchDetail.offers.attachment.errorTooLarge';
      if (err.status === 400) {
        // Beide 400er tragen denselben Problem-Typ, unterscheidbar ist nur der Titel.
        const body = err.error as {title?: unknown} | null;
        const title = typeof body?.title === 'string' ? body.title.toLowerCase() : '';
        return title.includes('abbreviation')
          ? 'benchDetail.offers.attachment.errorNoAbbreviation'
          : 'benchDetail.offers.attachment.errorRejected';
      }
    }
    return 'benchDetail.offers.attachment.errorUpload';
  }

  /**
   * Clone the entry for sending and normalise empty date strings to null.
   * Native date inputs yield '' when cleared, which the backend's LocalDate
   * fields cannot deserialise (would 400).
   */
  private buildPayload(): BenchEntryDTO {
    const clone = JSON.parse(JSON.stringify(this.entry)) as BenchEntryDTO;
    const emptyToNull = (v: string | null): string | null => (v ? v : null);
    clone.availableFrom = emptyToNull(clone.availableFrom);
    clone.commitmentFrom = emptyToNull(clone.commitmentFrom);
    clone.notes = withText(clone.notes);
    clone.notes.forEach(n => (n.date = emptyToNull(n.date) as string));
    const syncIds = this.projectSyncAccepted
      ? new Set(this.projectSyncOffers.map(o => o.id))
      : new Set<string | null>();
    // Dieselben Bereinigungen je Angebot wie bei „Angebot speichern“ (u. a. Anhang-Notiz aus Leerzeichen → null).
    clone.offers = clone.offers.map(o => normalizedOffer(o, !!o.id && syncIds.has(o.id)));
    return clone;
  }

  private flashSaved(area: SavedArea = 'all'): void {
    this.savedArea = area;
    this.showSaved = true;
    if (this.savedTimeout !== null) clearTimeout(this.savedTimeout);
    this.savedTimeout = setTimeout(() => {
      this.showSaved = false;
      this.savedTimeout = null;
    }, 1600);
  }
}
